import { Box } from '@leagueplatform/genesis-core';
import OnboardingRoutesWithProvider from '@leagueplatform/onboarding';
import { TENANT_ID } from 'common/constants';
import { getFeatureHighlightsContentfulId } from 'common/env';

export const OnboardingFeatureHighlights = ({
  onOnboardingComplete,
}: {
  onOnboardingComplete: (error?: Error) => void;
}) => (
  <Box
    css={{
      // Hide SDK back-arrow button (Geisinger flow is forward-only).
      // visibility (not display) so its 60px flex slot survives — the SDK's
      // slide centring assumes it, and the SDK itself hides via opacity.
      'button:has(img[src*="back-arrow"])': {
        visibility: 'hidden',
      },
      // Recolor SDK forward-arrow icon from white to Primary/Text Default (#004E70)
      'img[src*="forward-arrow"]': {
        filter:
          'brightness(0) saturate(100%) invert(20%) sepia(48%) saturate(1907%) hue-rotate(178deg) brightness(95%) contrast(101%)',
      },
      // Figma forward button is a 56px square (asset is a plain rect, no radius)
      'button:has(img[src*="forward-arrow"])': {
        borderRadius: 0,
      },
      // The SDK ships the forward button and pagination dots without a
      // :focus-visible style, so keyboard focus was invisible on both (WCAG
      // 2.4.7). Re-apply Genesis' own focus ring (outline + inset box-shadow,
      // matching the Skip button and every other Genesis control) using the
      // explicit $scale$token syntax so the tokens resolve inside the shorthand.
      'button:has(img[src*="forward-arrow"]):focus-visible, li button:focus-visible':
        {
          outline:
            '$borderWidths$outerFocus solid $colors$interactiveFocusOuter',
          boxShadow:
            'inset 0 0 0 $borderWidths$innerFocus $colors$interactiveFocusInner',
        },
      // Symmetric padding so the illustration centres inside ImagePanel (SDK ships $five on the right)
      'div:has(> .feature-slide_illustration), div:has(> div > .feature-slide_illustration)':
        {
          paddingInlineEnd: '$one',
        },
      // Centre the illustration over the mint panel. The panel is absolute at
      // viewport-left while the illustration sits in the SDK's centred Container,
      // so no flex primitive can bridge them. Coefficients are calibrated against
      // the live SDK layout (exact at 1280/1440/1600); the regime flips at 1600px
      // where Container hits its 1440px maxWidth cap and stops tracking viewport.
      '.feature-slide_illustration': {
        transform: 'translateX(calc(1.25vw - 84.7px))',
      },
      // Below the 1280px Genesis desktop breakpoint the panel switches to
      // 5/12 of viewport — different slope, refitted (≤1024 the SDK's panel
      // min-width clamp takes over; small residual drift there is accepted)
      '@media (max-width: 1279px)': {
        '.feature-slide_illustration': {
          transform: 'translateX(calc(-2.89vw - 25px))',
        },
      },
      // Above 1600px the SDK Container hits its 1440px maxWidth cap and stops
      // tracking the viewport, so the slope inverts — refitted for that regime
      '@media (min-width: 1600px)': {
        '.feature-slide_illustration': {
          transform: 'translateX(calc(335.3px - 25vw))',
        },
      },
      // Active carousel dot uses brand action colour (Figma paints League-blue
      // #007CB2 from the un-rethemed library component — token keeps it on-brand)
      'button[aria-current="true"] div': {
        backgroundColor: '$interactiveActionPrimary',
        borderColor: '$interactiveActionPrimary',
      },
      // Skip per Figma: Lato Bold 14/20, Interactive/Action Subdued.
      // Legacy Genesis global styles set html font-size to 62.5%, so 1rem = 10px
      'button[aria-describedby="onboarding-fh-title"]': {
        fontSize: '1.4rem',
        lineHeight: '2rem',
        color: '$interactiveActionSubdued',
      },
    }}
  >
    <OnboardingRoutesWithProvider
      contentfulId={getFeatureHighlightsContentfulId()}
      tenantId={TENANT_ID}
      onOnboardingComplete={onOnboardingComplete}
    />
  </Box>
);
